<template>
  <div>
    <paper-modal-container :is-visible="true" :title="getTitle()" :hide-close-button="isPreviewMode"
      @close="$router.back()">
      <div v-if="loading" class="loading">로딩 중...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="liveData && formInfo" class="form-wrapper">
        <!-- 예매 완료 안내 (viewMode일 때) -->
        <div v-if="isViewMode && userReservation" class="reservation-confirm-notice">
          ✅ 예매가 완료되었습니다
        </div>

        <!-- 미리보기 안내 -->
        <div v-if="isPreviewMode" class="preview-notice">
          <span class="preview-notice-icon">✓</span>
          <div class="preview-notice-content">
            <span class="preview-notice-label">미리보기</span>
            <p class="preview-notice-text">예매 폼 미리보기 화면입니다. 실제 예매 시와 동일한 양식으로 표시됩니다.</p>
          </div>
        </div>

        <!-- 공연 기본정보 섹션 -->
        <section class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">공연 기본정보</h2>
            <p class="section-description">예매할 공연의 기본 정보입니다</p>
          </div>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">공연명</span>
              <span class="info-value">{{ liveData.title }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">공연일시</span>
              <span class="info-value">{{ liveData.date }}</span>
            </div>
            <div class="info-row" v-if="liveData.entryTime">
              <span class="info-label">입장시간</span>
              <span class="info-value">{{ liveData.entryTime }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">장소</span>
              <span class="info-value">{{ liveData.place }}</span>
            </div>
            <div class="info-row"
              v-if="liveData.lineup && (Array.isArray(liveData.lineup) ? liveData.lineup.length > 0 : true)">
              <span class="info-label">라인업</span>
              <span class="info-value">
                {{
                  Array.isArray(liveData.lineup)
                    ? liveData.lineup.join(", ")
                    : liveData.lineup
                }}
              </span>
            </div>
            <div class="info-row" v-if="liveData.linkookPrice">
              <span class="info-label">예매가격</span>
              <span class="info-value">{{ liveData.linkookPrice }}</span>
            </div>
          </div>
        </section>

        <!-- 판매자 정보 섹션 -->
        <section v-if="liveData.organizerName || liveData.organizerContact" class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">판매자 정보</h2>
          </div>
          <div class="info-box">
            <div v-if="liveData.organizerName" class="info-row">
              <span class="info-label">판매자명</span>
              <span class="info-value">{{ liveData.organizerName }}</span>
            </div>
            <div v-if="liveData.organizerContact" class="info-row">
              <span class="info-label">연락처</span>
              <span class="info-value">{{ liveData.organizerContact }}</span>
            </div>
          </div>
        </section>

        <!-- 커스텀 안내사항 섹션 -->
        <section v-if="formInfo.noticeFields && formInfo.noticeFields.length > 0"
          class="form-section-block custom-notice-section">
          <div class="section-header">
            <h2 class="section-title">추가 안내사항</h2>
            <p class="section-description">관리자가 작성한 안내사항입니다</p>
          </div>
          <div v-for="(notice, index) in formInfo.noticeFields" :key="'notice-' + index" class="custom-notice-item">
            <h3 class="custom-notice-title">{{ notice.title }}</h3>
            <div class="notice-content" v-html="formatText(notice.content)"></div>
          </div>
        </section>


        <!-- 커스텀 질문 섹션 -->
        <section v-if="formInfo.customFields && formInfo.customFields.length > 0"
          class="form-section-block custom-questions-section">
          <div class="section-header">
            <h2 class="section-title">공연 폼 작성하기</h2>
            <p class="section-description">아래 질문에 답해 주세요</p>
          </div>
          <div class="custom-fields-section">
            <div v-for="(field, index) in formInfo.customFields" :key="'custom-' + index"
              class="form-group custom-field-item">
              <label :class="{ required: field.required }">
                {{ field.title }}
              </label>
              <template v-if="field.imageUrls && field.imageUrls.length > 0">
                <img v-for="(url, imgIdx) in field.imageUrls" :key="imgIdx" :src="url" :alt="field.title"
                  class="custom-field-image zoomable" @click="openLightbox(url)" />
              </template>
              <img v-else-if="field.imageUrl" :src="field.imageUrl" :alt="field.title"
                class="custom-field-image zoomable" @click="openLightbox(field.imageUrl)" />
              <p v-if="field.description" class="field-description">{{ field.description }}</p>

              <!-- 1. 단답형 -->
              <div v-if="field.type === 'text'" class="custom-field-text">
                <input v-model="formData.customFieldAnswers[index]" type="text" :placeholder="field.title"
                  :required="field.required" :disabled="submitting || isPreviewMode || isViewMode" class="text-input" />
              </div>

              <!-- 2. 장문형 -->
              <div v-else-if="field.type === 'textarea'" class="custom-field-textarea">
                <textarea v-model="formData.customFieldAnswers[index]" rows="4" :placeholder="field.title"
                  :required="field.required" :disabled="submitting || isPreviewMode || isViewMode"
                  class="textarea-input"></textarea>
              </div>

              <!-- 3. 객관식 (버튼 스타일) -->
              <div v-else-if="field.type === 'radio'" class="custom-field-radio">
                <div class="radio-button-group">
                  <button type="button" v-for="(option, optionIndex) in (field.options || []).filter(Boolean)"
                    :key="optionIndex" class="radio-option-btn"
                    :class="{ selected: formData.customFieldAnswers[index] === option }"
                    @click="formData.customFieldAnswers[index] = option"
                    :disabled="submitting || isPreviewMode || isViewMode">
                    {{ option }}
                  </button>
                </div>
                <input v-if="field.required" type="hidden" :value="formData.customFieldAnswers[index]"
                  :required="field.required" />
              </div>

              <!-- 4. 체크박스 -->
              <div v-else-if="field.type === 'checkbox'" class="custom-field-checkbox">
                <div class="checkbox-group">
                  <label v-for="(option, optionIndex) in (field.options || []).filter(Boolean)" :key="optionIndex"
                    class="checkbox-label">
                    <input type="checkbox" :value="option" @change="toggleCheckbox(index, option)"
                      :checked="isCheckboxChecked(index, option)"
                      :disabled="submitting || isPreviewMode || isViewMode" />
                    <span>{{ option }}</span>
                  </label>
                </div>
              </div>

              <!-- 타입 없음/미지원 시 단답형으로 폴백 -->
              <div v-else class="custom-field-text">
                <input v-model="formData.customFieldAnswers[index]" type="text" :placeholder="field.title"
                  :required="field.required" :disabled="submitting || isPreviewMode || isViewMode" class="text-input" />
              </div>
            </div>
          </div>
        </section>

        <!-- 티켓 패키지 구성 섹션 -->
        <section v-if="hasRewardTiers" class="form-section-block reward-tiers-section">
          <div class="section-header">
            <h2 class="section-title">후원 티켓 패키지</h2>
            <p class="section-description">원하시는 패키지를 선택해 주세요</p>
          </div>
          <div v-for="(tier, index) in formInfo.rewardTiers" :key="'reward-' + index" class="reward-tier-card"
            :class="{ 'reward-selected': formData.rewardAnswers[index] && formData.rewardAnswers[index].quantity > 0 }"
            @click="toggleReward(index)">
            <div v-if="tier.imageUrls && tier.imageUrls.length > 0" class="reward-tier-images" @click.stop>
              <img v-for="(url, imgIdx) in tier.imageUrls" :key="imgIdx" :src="url" :alt="tier.name"
                class="reward-tier-image zoomable" @click="openLightbox(url)" />
            </div>
            <div class="reward-tier-header">
              <span class="reward-check"
                v-if="formData.rewardAnswers[index] && formData.rewardAnswers[index].quantity > 0">✓</span>
              <span class="reward-tier-name">{{ tier.name }}</span>
            </div>
            <div class="reward-tier-price">{{ formatWon(tier.price) }} 패키지</div>
            <p v-if="tier.description" class="reward-tier-desc">{{ tier.description }}</p>
            <ul class="reward-tier-items">
              <li v-for="(item, iIdx) in tier.items" :key="iIdx">
                {{ item.name }} (x{{ item.quantity }})
              </li>
            </ul>
            <div v-if="formData.rewardAnswers[index] && formData.rewardAnswers[index].quantity > 0"
              class="reward-qty-row" @click.stop>
              <button type="button" class="reward-qty-btn" @click.stop="decrementReward(index)"
                :disabled="submitting || isPreviewMode || isViewMode || formData.rewardAnswers[index].quantity <= 0">−</button>
              <span class="reward-qty-value">{{ formData.rewardAnswers[index].quantity }}</span>
              <button type="button" class="reward-qty-btn" @click.stop="incrementReward(index)"
                :disabled="submitting || isPreviewMode || isViewMode || (tier.maxSelection && formData.rewardAnswers[index].quantity >= tier.maxSelection)">+</button>
              <span class="reward-subtotal">{{ formatWon(tier.price * formData.rewardAnswers[index].quantity) }}</span>
            </div>
          </div>
        </section>

        <!-- 추가구매항목 섹션 -->
        <section v-if="hasPurchaseItems" class="form-section-block purchase-items-section">
          <div class="section-header">
            <h2 class="section-title">추가구매항목</h2>
            <p class="section-description">원하시는 항목을 선택해 주세요</p>
          </div>
          <div v-for="(item, index) in formInfo.purchaseItems" :key="'purchase-' + index" class="purchase-item-card">
            <div class="purchase-item-top">
              <div v-if="(item.imageUrls && item.imageUrls.length > 0) || item.imageUrl"
                class="purchase-item-images-wrapper">
                <template v-if="item.imageUrls && item.imageUrls.length > 0">
                  <img v-for="(url, imgIdx) in item.imageUrls" :key="imgIdx" :src="url" :alt="item.name"
                    class="purchase-item-image zoomable" @click="openLightbox(url)" />
                </template>
                <img v-else-if="item.imageUrl" :src="item.imageUrl" :alt="item.name"
                  class="purchase-item-image zoomable" @click="openLightbox(item.imageUrl)" />
              </div>
              <div class="purchase-item-info">
                <div class="purchase-item-name">
                  {{ item.name }}
                  <span v-if="item.required" class="purchase-required-badge">필수</span>
                </div>
                <div v-if="item.description" class="purchase-item-desc">{{ item.description }}</div>
                <div class="purchase-item-price">{{ formatWon(item.price) }}</div>
              </div>
            </div>

            <!-- 수량 선택 -->
            <div class="purchase-qty-row">
              <div class="purchase-qty-control">
                <button type="button" class="purchase-qty-btn" @click="decrementPurchase(index)"
                  :disabled="submitting || isPreviewMode || isViewMode || formData.purchaseAnswers[index].quantity <= (item.required ? 1 : 0)">
                  −
                </button>
                <span class="purchase-qty-value">{{ formData.purchaseAnswers[index] ?
                  formData.purchaseAnswers[index].quantity : 0 }}</span>
                <button type="button" class="purchase-qty-btn" @click="incrementPurchase(index)"
                  :disabled="submitting || isPreviewMode || isViewMode || (item.maxQuantity > 0 && formData.purchaseAnswers[index].quantity >= item.maxQuantity)">
                  +
                </button>
              </div>
              <span v-if="formData.purchaseAnswers[index] && formData.purchaseAnswers[index].quantity > 0"
                class="purchase-subtotal">
                {{ formatWon(item.price * formData.purchaseAnswers[index].quantity) }}
              </span>
            </div>

            <!-- 옵션 입력 (수량 1 이상일 때만) -->
            <div
              v-if="item.hasOption && formData.purchaseAnswers[index] && formData.purchaseAnswers[index].quantity > 0"
              class="purchase-option-area">
              <label :class="{ required: item.optionRequired }">{{ item.optionTitle }}</label>
              <p v-if="item.optionDescription" class="field-description">{{ item.optionDescription }}</p>

              <div v-if="item.optionType === 'text'" class="custom-field-text">
                <input v-model="formData.purchaseAnswers[index].optionAnswer" type="text"
                  :placeholder="item.optionTitle" :disabled="submitting || isPreviewMode || isViewMode"
                  class="text-input" />
              </div>
              <div v-else-if="item.optionType === 'textarea'" class="custom-field-textarea">
                <textarea v-model="formData.purchaseAnswers[index].optionAnswer" rows="3"
                  :placeholder="item.optionTitle" :disabled="submitting || isPreviewMode || isViewMode"
                  class="textarea-input"></textarea>
              </div>
              <div v-else-if="item.optionType === 'radio'" class="custom-field-radio">
                <div class="radio-button-group">
                  <button type="button" v-for="(option, optIdx) in (item.options || []).filter(Boolean)" :key="optIdx"
                    class="radio-option-btn"
                    :class="{ selected: formData.purchaseAnswers[index].optionAnswer === option }"
                    @click="formData.purchaseAnswers[index].optionAnswer = option"
                    :disabled="submitting || isPreviewMode || isViewMode">
                    {{ option }}
                  </button>
                </div>
              </div>
              <div v-else-if="item.optionType === 'checkbox'" class="custom-field-checkbox">
                <div class="checkbox-group">
                  <label v-for="(option, optIdx) in (item.options || []).filter(Boolean)" :key="optIdx"
                    class="checkbox-label">
                    <input type="checkbox" :value="option" @change="togglePurchaseCheckbox(index, option)"
                      :checked="isPurchaseCheckboxChecked(index, option)"
                      :disabled="submitting || isPreviewMode || isViewMode" />
                    <span>{{ option }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 입금 계좌 섹션 -->
        <section class="form-section-block">
          <div class="section-header">
            <h2 class="section-title">입금 계좌</h2>
            <p class="section-description">입금 시 아래 계좌로 송금해 주세요</p>
            <p v-if="depositNicknameText" class="deposit-nickname-notice">{{ depositNicknameText }}</p>
          </div>
          <div class="info-box">
            <div class="info-row">
              <span class="info-label">은행</span>
              <span class="info-value">국민은행</span>
              <!-- <span class="info-value">{{ liveData.bankName }}</span> -->
            </div>
            <div class="info-row">
              <span class="info-label">계좌번호</span>
              <span class="info-value info-value-account info-value-account-wrap">
                <span class="account-number-text">009901-04-203973</span>
                <!-- <span class="account-number-text">{{ liveData.accountNumber }}</span> -->
                <button type="button" class="copy-btn-icon" @click="copyAccountNumber('00990104203973')" title="계좌번호 복사"
                  aria-label="계좌번호 복사">
                  <!-- <button type="button" class="copy-btn-icon" @click="copyAccountNumber(liveData.accountNumber)"
                  title="계좌번호 복사" aria-label="계좌번호 복사"> -->
                  <img src="@/assets/images/file_copy_200.svg" alt="복사" />
                </button>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">예금주</span>
              <span class="info-value">이민경 (링꾹)</span>
              <!-- <span class="info-value">{{ liveData.accountHolder }}</span> -->
            </div>
          </div>
          <p class="deposit-warning-notice">※ 입금자명을 꼭 다시 한 번 확인해 주세요!</p>
        </section>


        <!-- 취소/환불 규정 섹션 -->
        <section class="form-section-block cancel-policy-section">
          <div class="section-header">
            <h2 class="section-title">취소/환불 규정</h2>
          </div>
          <!-- 주최자 직접 작성 규정 -->
          <div v-if="formInfo.cancelPolicy && formInfo.cancelPolicy.enabled && formInfo.cancelPolicy.content"
            class="cancel-policy-content" v-html="formatText(formInfo.cancelPolicy.content)">
          </div>
          <!-- 기본 규정 (직접 작성 규정 없을 때) -->
          <template v-else>
            <ul class="cancel-policy-default-list">
              <li>공연 시작일 3일 전까지: 결제 금액 전액 환불</li>
              <li>공연 시작일 2일 전: 결제 금액의 70% 환불</li>
              <li>공연 시작일 1일 전 및 당일 이후: 환불 불가</li>
            </ul>
            <p class="cancel-policy-default-note">링꾹라이브 이용약관 4조에 따른 기본 규정입니다.</p>
          </template>
        </section>

        <form @submit.prevent="handleSubmit">
          <!-- 예매 기간 안내/차단 (관리자는 예외) -->
          <div v-if="!isPreviewMode && !isViewMode && !isAdmin && !canSubmitReservationNow" class="reservation-guard">
            <p v-if="isReservationClosed" class="reservation-guard-text">예매가 종료되었습니다.</p>
            <p v-else class="reservation-guard-text">
              예매대기<span v-if="reservationCountdownText"> · {{ reservationCountdownText }}</span>
            </p>
          </div>

          <!-- 연락처 섹션 -->
          <section class="form-section-block">
            <div class="section-header">
              <h2 class="section-title">연락처</h2>
              <p class="section-description">예매 관련 연락을 받으실 전화번호를 입력해 주세요</p>
            </div>
            <div class="form-group">
              <label class="required">연락받으실 전화번호 <span class="label-hint">공연 종료 후 폐기됩니다</span></label>
              <input v-model="formData.phone" type="tel" placeholder="01012345678" class="text-input"
                :disabled="submitting || isPreviewMode || isViewMode" maxlength="13"
                @input="formData.phone = formData.phone.replace(/[^0-9-]/g, '')" />
            </div>
          </section>

          <!-- 예매 신청 섹션 (패키지가 없을 때만 표시) -->
          <section v-if="!hasRewardTiers" class="form-section-block">
            <div class="section-header">
              <h2 class="section-title">예매 신청</h2>
              <p class="section-description">티켓 매수를 선택해 주세요</p>
            </div>
            <div class="form-group">
              <label class="required">티켓 매수 <span class="quantity-limit-hint">(인당 최대 {{ perPersonLimit
                  }}장)</span></label>
              <div class="quantity-button-group">
                <button type="button" v-for="n in 5" :key="n" class="quantity-btn"
                  :class="{ selected: formData.quantity == n, 'disabled-by-limit': n > perPersonLimit }"
                  @click="formData.quantity = n"
                  :disabled="submitting || isPreviewMode || isViewMode || (!isAdmin && !canSubmitReservationNow) || n > perPersonLimit">
                  {{ n }}장
                </button>
              </div>
              <input type="hidden" v-model="formData.quantity" required />
            </div>
          </section>

          <!-- 최종 입금 금액 섹션 -->
          <section v-if="shouldShowTotalDeposit" class="form-section-block total-deposit-section">
            <div class="section-header">
              <h2 class="section-title">최종 입금 금액</h2>
              <p class="section-description">선택한 항목 기준으로 계산됩니다</p>
            </div>
            <div class="info-box">
              <div class="total-deposit-compact">
                <div class="total-deposit-amount">{{ totalDepositAmountText }}</div>
                <div v-if="totalDepositAmountSubText" class="total-deposit-calc">
                  {{ totalDepositAmountSubText }}
                </div>
              </div>

              <!-- 구매 내역 상세 -->
              <div v-if="orderSummaryItems.length" class="order-summary">
                <div class="order-summary-title">구매 내역</div>
                <div v-for="(item, idx) in orderSummaryItems" :key="'os-' + idx" class="order-summary-row">
                  <div class="order-summary-name">{{ item.name }}</div>
                  <div class="order-summary-detail">{{ item.qty }}개 × {{ formatWon(item.unitPrice) }} = <strong>{{
                    formatWon(item.subtotal) }}</strong></div>
                </div>
                <div class="order-summary-total-row">
                  <span class="order-summary-total-label">합계</span>
                  <span class="order-summary-total-value">{{ totalDepositAmountText }}</span>
                </div>
              </div>
            </div>
          </section>

          <div v-if="!isViewMode" class="form-actions">
            <button type="submit" class="submit-btn"
              :disabled="submitting || !isFormValid || isPreviewMode || (!isAdmin && !canSubmitReservationNow)"
              :class="{ 'preview-disabled': isPreviewMode }">
              {{ submitting ? "제출 중..." : "예매 신청하기" }}
            </button>
          </div>
        </form>

        <!-- 예매 상태 정보 (viewMode일 때만) -->
        <div v-if="isViewMode && userReservation" class="status-info">
          <div class="status-row">
            <span class="status-label">예매 상태</span>
            <span class="status-value" :class="`status-${userReservation.status}`">
              {{ getStatusText(userReservation.status) }}
            </span>
          </div>
          <div class="status-row">
            <span class="status-label">입금 확인</span>
            <span class="status-value" :class="{ 'payment-confirmed': userReservation.paymentConfirmed }">
              {{ userReservation.paymentConfirmed ? "✓ 확인됨" : "미확인" }}
            </span>
          </div>
          <div class="status-row">
            <span class="status-label">티켓 매수</span>
            <span class="status-value">{{ userReservation.quantity }}장</span>
          </div>
          <!-- 커스텀 필드 답변 표시 -->
          <template
            v-if="formInfo.customFields && formInfo.customFields.length > 0 && userReservation.customFieldAnswers">
            <div v-for="(field, index) in formInfo.customFields" :key="'view-' + index" class="status-row">
              <span class="status-label">{{ field.title }}</span>
              <span class="status-value">{{ formatCustomFieldAnswer(userReservation.customFieldAnswers[index],
                field.type) }}</span>
            </div>
          </template>
          <!-- 티켓 패키지 답변 표시 -->
          <template v-if="hasRewardTiers && userReservation.rewardAnswers">
            <div v-for="ra in userReservation.rewardAnswers" :key="'view-reward-' + ra.tierIndex">
              <div v-if="formInfo.rewardTiers[ra.tierIndex]" class="status-row">
                <span class="status-label">{{ formInfo.rewardTiers[ra.tierIndex].name }}</span>
                <span class="status-value">
                  {{ ra.quantity }}개
                  ({{ formatWon(formInfo.rewardTiers[ra.tierIndex].price * ra.quantity) }})
                </span>
              </div>
            </div>
          </template>
          <!-- 추가구매항목 답변 표시 -->
          <template v-if="hasPurchaseItems && userReservation.purchaseAnswers">
            <div v-for="(item, index) in formInfo.purchaseItems" :key="'view-purchase-' + index">
              <div v-if="userReservation.purchaseAnswers[index] && userReservation.purchaseAnswers[index].quantity > 0"
                class="status-row">
                <span class="status-label">{{ item.name }}</span>
                <span class="status-value">
                  {{ userReservation.purchaseAnswers[index].quantity }}개
                  ({{ formatWon(item.price * userReservation.purchaseAnswers[index].quantity) }})
                  <template v-if="userReservation.purchaseAnswers[index].optionAnswer">
                    · {{ Array.isArray(userReservation.purchaseAnswers[index].optionAnswer)
                      ? userReservation.purchaseAnswers[index].optionAnswer.join(', ')
                      : userReservation.purchaseAnswers[index].optionAnswer }}
                  </template>
                </span>
              </div>
            </div>
          </template>
        </div>

        <p v-if="isViewMode && userReservation" class="status-notice">
          *문의사항이 있으시면 관리자에게 문의해주세요
        </p>

        <button v-if="isViewMode && userReservation" class="dm-button" @click="sendDM">
          <img class="x-icon" src="@/assets/images/xlogowhite.png" alt="X" />
          링꾹라이브 DM 보내기
        </button>

        <!-- 제출 완료 메시지 -->
        <div v-if="submitSuccess" class="success-message">
          <p>✅ 예매 신청이 완료되었습니다!</p>
          <p>마이 프로필에서 예매 확인 가능합니다</p>
        </div>
      </div>
    </paper-modal-container>

    <!-- 이미지 라이트박스 -->
    <transition name="lightbox-fade">
      <div v-if="lightboxUrl" class="lightbox-overlay" @click.self="closeLightbox">
        <button class="lightbox-close" @click="closeLightbox" aria-label="닫기">✕</button>
        <img :src="lightboxUrl" class="lightbox-img" alt="이미지 전체보기" @click.stop />
      </div>
    </transition>
  </div>
</template>

<script>
import { firestore } from "../../../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { createReservation } from "@/utils/write/ticket";
import {
  getTicketReservationsByLiveIdAndRegId,
  getTotalReservedQuantityForLive,
} from "@/utils/read/ticket";
import { trackReservationComplete } from "@/utils/activityTracker";
import { useUserStore } from "@/store/user";
import PaperModalContainer from "@/components/modal/PaperModalContainer.vue";

export default {
  name: "TicketForm",
  components: {
    PaperModalContainer,
  },
  data() {
    return {
      userStore: useUserStore(),
      liveId: this.$route.params.id,
      isPreviewMode: this.$route.query.preview === "true",
      isViewMode: this.$route.query.mode === "view",
      liveData: null,
      formInfo: null,
      loading: true,
      error: null,
      submitting: false,
      submitSuccess: false,
      userReservation: null,
      nowTs: Date.now(),
      nowIntervalId: null,
      lightboxUrl: null,
      formData: {
        quantity: "",
        phone: "",
        customFieldAnswers: {},
        purchaseAnswers: [],
        rewardAnswers: [],
        paymentInfoChecked: false,
      },
    };
  },
  computed: {
    isAdmin() {
      return this.userStore.auth === "admin";
    },

    reservationStartDateObj() {
      const raw = this.liveData?.reservationStartDate ?? this.formInfo?.reservationStartDate ?? null;
      if (!raw) return null;
      if (raw?.toDate && typeof raw.toDate === "function") return raw.toDate();
      if (raw instanceof Date) return raw;
      if (typeof raw === "string") {
        const m = /^(\d{4})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/.exec(raw);
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
      }
      return null;
    },
    reservationEndDateObj() {
      const raw = this.liveData?.reservationEndDate ?? this.formInfo?.reservationEndDate ?? null;
      if (!raw) return null;
      if (raw?.toDate && typeof raw.toDate === "function") return raw.toDate();
      if (raw instanceof Date) return raw;
      if (typeof raw === "string") {
        const m = /^(\d{4})\.(\d{2})\.(\d{2}) (\d{2}):(\d{2})$/.exec(raw);
        if (!m) return null;
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
      }
      return null;
    },
    isReservationOpened() {
      // 시작일이 없으면 열린 것으로 간주
      if (!this.reservationStartDateObj) return true;
      return this.nowTs >= this.reservationStartDateObj.getTime();
    },
    isReservationClosed() {
      // 종료일이 없으면 닫히지 않은 것으로 간주
      if (!this.reservationEndDateObj) return false;
      return this.nowTs > this.reservationEndDateObj.getTime();
    },
    canSubmitReservationNow() {
      // 관리자는 예매 기간과 무관하게 가능
      if (this.isAdmin) return true;
      if (this.isReservationClosed) return false;
      if (!this.isReservationOpened) return false;
      return true;
    },
    reservationCountdownText() {
      if (this.isReservationOpened) return "";
      if (!this.reservationStartDateObj) return "";
      const diff = this.reservationStartDateObj.getTime() - this.nowTs;
      if (diff <= 0) return "";
      const totalSeconds = Math.floor(diff / 1000);
      const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
      const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
      const ss = String(totalSeconds % 60).padStart(2, "0");
      return `${hh}:${mm}:${ss}`;
    },
    // 입금 시 닉네임 안내 문구
    depositNicknameText() {
      if (this.userStore.nickname) {
        return `"${this.userStore.nickname}"으로 입금해 주셔야 입금 확인됩니다`;
      }
      if (this.isPreviewMode) {
        return "예매 시 로그인한 닉네임으로 입금해 주세요";
      }
      return "";
    },
    // 인당 구매제한 (1~5, 기본 5)
    perPersonLimit() {
      const limit = this.liveData?.linkookPerPersonLimit;
      if (limit >= 1 && limit <= 5) return limit;
      return 5;
    },
    hasRewardTiers() {
      return Array.isArray(this.formInfo?.rewardTiers) && this.formInfo.rewardTiers.length > 0;
    },
    rewardItemsTotal() {
      if (!this.hasRewardTiers || !this.formData.rewardAnswers.length) return 0;
      return this.formInfo.rewardTiers.reduce((sum, tier, i) => {
        const qty = this.formData.rewardAnswers[i]?.quantity || 0;
        return sum + (Number(tier.price) || 0) * qty;
      }, 0);
    },
    hasPurchaseItems() {
      return Array.isArray(this.formInfo?.purchaseItems) && this.formInfo.purchaseItems.length > 0;
    },
    purchaseItemsTotal() {
      if (!this.hasPurchaseItems || !this.formData.purchaseAnswers.length) return 0;
      return this.formInfo.purchaseItems.reduce((sum, item, i) => {
        const qty = this.formData.purchaseAnswers[i]?.quantity || 0;
        return sum + (Number(item.price) || 0) * qty;
      }, 0);
    },
    // 폼 유효성 검사
    isFormValid() {
      const phoneClean = (this.formData.phone || "").replace(/[^0-9]/g, "");
      const baseValid =
        this.formData.quantity !== "" &&
        this.formData.quantity !== undefined &&
        phoneClean.length >= 9;

      // 입금 시 주의사항이 있으면 체크박스 확인 필요
      if (this.formInfo?.paymentInfo) {
        if (!this.formData.paymentInfoChecked) return false;
      }

      // 커스텀 필드 필수 항목 체크
      if (this.formInfo?.customFields && this.formInfo.customFields.length > 0) {
        for (let i = 0; i < this.formInfo.customFields.length; i++) {
          const field = this.formInfo.customFields[i];
          if (field.required) {
            const answer = this.formData.customFieldAnswers[i];
            if (!answer || (typeof answer === 'string' && answer.trim() === '') ||
              (Array.isArray(answer) && answer.length === 0)) {
              return false;
            }
          }
        }
      }

      // 추가구매항목 필수 항목 및 옵션 체크
      if (this.hasPurchaseItems) {
        for (let i = 0; i < this.formInfo.purchaseItems.length; i++) {
          const item = this.formInfo.purchaseItems[i];
          const ans = this.formData.purchaseAnswers[i];
          if (item.required && (!ans || ans.quantity < 1)) return false;
          if (item.hasOption && item.optionRequired && ans && ans.quantity > 0) {
            if (item.optionType === "checkbox") {
              if (!Array.isArray(ans.optionAnswer) || ans.optionAnswer.length === 0) return false;
            } else {
              if (!ans.optionAnswer || String(ans.optionAnswer).trim() === "") return false;
            }
          }
        }
      }

      return baseValid;
    },

    unitTicketPriceNumber() {
      const rawPrice = this.liveData?.linkookPrice ?? "";
      if (typeof rawPrice === "number") return Number.isFinite(rawPrice) ? rawPrice : null;
      if (typeof rawPrice !== "string") return null;
      const onlyDigits = rawPrice.replace(/[^\d]/g, "");
      if (!onlyDigits) return null;
      const n = Number(onlyDigits);
      return Number.isFinite(n) ? n : null;
    },
    totalDepositAmountNumber() {
      const quantity = Number(this.formData.quantity || 0);
      const unit = this.unitTicketPriceNumber;
      const ticketTotal = unit && quantity ? unit * quantity : null;
      const goodsTotal = this.purchaseItemsTotal || 0;
      const rewardTotal = this.rewardItemsTotal || 0;
      if (ticketTotal === null && goodsTotal === 0 && rewardTotal === 0) return null;
      return (ticketTotal || 0) + goodsTotal + rewardTotal;
    },
    formatWon() {
      return (n) => `${new Intl.NumberFormat("ko-KR").format(n)}원`;
    },
    orderSummaryItems() {
      const items = [];
      const quantity = Number(this.formData.quantity || 0);
      const unit = this.unitTicketPriceNumber;
      if (!this.hasRewardTiers && unit && quantity) {
        items.push({ name: '티켓', qty: quantity, unitPrice: unit, subtotal: unit * quantity });
      }
      if (this.hasRewardTiers && this.formInfo.rewardTiers) {
        this.formInfo.rewardTiers.forEach((tier, i) => {
          const qty = this.formData.rewardAnswers[i]?.quantity || 0;
          if (qty > 0) {
            const price = Number(tier.price) || 0;
            items.push({ name: tier.name, qty, unitPrice: price, subtotal: price * qty });
          }
        });
      }
      if (this.hasPurchaseItems && this.formInfo.purchaseItems) {
        this.formInfo.purchaseItems.forEach((item, i) => {
          const qty = this.formData.purchaseAnswers[i]?.quantity || 0;
          if (qty > 0) {
            const price = Number(item.price) || 0;
            items.push({ name: item.name, qty, unitPrice: price, subtotal: price * qty });
          }
        });
      }
      return items;
    },
    shouldShowTotalDeposit() {
      const quantity = Number(this.formData.quantity || 0);
      const rawPrice = this.liveData?.linkookPrice ?? "";
      if (this.hasPurchaseItems && this.purchaseItemsTotal > 0) return true;
      if (this.hasRewardTiers && this.rewardItemsTotal > 0) return true;
      return !!(quantity && rawPrice);
    },
    totalDepositAmountText() {
      const total = this.totalDepositAmountNumber;
      if (total !== null) return this.formatWon(total);
      const quantity = Number(this.formData.quantity || 0);
      const rawPrice = this.liveData?.linkookPrice ?? "";
      if (!quantity || !rawPrice) return "";
      return `${rawPrice} (×${quantity}매)`;
    },
    totalDepositAmountSubText() {
      const quantity = Number(this.formData.quantity || 0);
      const rawPrice = this.liveData?.linkookPrice ?? "";
      const unit = this.unitTicketPriceNumber;
      const goodsTotal = this.purchaseItemsTotal || 0;
      const rewardTotal = this.rewardItemsTotal || 0;
      const lines = [];
      if (!this.hasRewardTiers && quantity && rawPrice) {
        if (unit) lines.push(`티켓 ${this.formatWon(unit)} × ${quantity}매`);
        else lines.push(`티켓 ${rawPrice} × ${quantity}매`);
      }
      if (rewardTotal > 0) lines.push(`패키지 ${this.formatWon(rewardTotal)}`);
      if (goodsTotal > 0) lines.push(`추가구매 ${this.formatWon(goodsTotal)}`);
      return lines.join(" + ");
    },
  },
  async mounted() {
    await this.loadData();

    if (!this.isAdmin && !this.isPreviewMode && !this.isViewMode && this.liveData) {
      if (!this.canSubmitReservationNow) {
        this.$alert("해당 라이브의 판매 기간이 아닙니다.");
        this.$router.push("/");
        return;
      }
    }

    await this.loadUserReservation();

    // 예매 시작/종료 시간에 맞춰 상태가 자동 갱신되도록 현재시간을 주기적으로 업데이트
    // (관리자/미리보기/확인 모드 제외)
    if (!this.isAdmin && !this.isPreviewMode && !this.isViewMode) {
      this.nowIntervalId = window.setInterval(() => {
        this.nowTs = Date.now();
      }, 1000);
    }

    this._onKeydown = (e) => {
      if (e.key === "Escape" && this.lightboxUrl) this.closeLightbox();
    };
    window.addEventListener("keydown", this._onKeydown);
  },
  beforeUnmount() {
    if (this.nowIntervalId) {
      clearInterval(this.nowIntervalId);
      this.nowIntervalId = null;
    }
    if (this._onKeydown) {
      window.removeEventListener("keydown", this._onKeydown);
    }
  },
  methods: {
    openLightbox(url) {
      this.lightboxUrl = url;
    },
    closeLightbox() {
      this.lightboxUrl = null;
    },
    getTitle() {
      if (this.isViewMode && this.userReservation) {
        return "예매 확인";
      }
      return "예매 신청서";
    },
    // 데이터 로드
    async loadData() {
      try {
        this.loading = true;
        this.error = null;
        // 공연 정보 가져오기
        const liveDoc = await getDoc(doc(firestore, "lives", this.liveId));
        if (!liveDoc.exists()) {
          console.error("공연 정보를 찾을 수 없습니다. Live ID:", this.liveId);
          this.error = "공연 정보를 찾을 수 없습니다.";
          return;
        }
        this.liveData = liveDoc.data();

        // 예매 폼 정보 가져오기
        const formDoc = await getDoc(doc(firestore, "ticketForm", this.liveId));
        if (formDoc.exists()) {
          this.formInfo = formDoc.data();
        } else {
          // 폼 정보가 없어도 기본값으로 설정
          console.warn("예매 폼 정보가 아직 설정되지 않았습니다. 기본값 사용.");
          this.formInfo = {
            reservationStartDate: null,
            reservationEndDate: null,
            paymentInfo: null,
            cancelInfo: null,
            entryInfo: null,
          };
        }

        // customFields가 없으면 빈 배열로 초기화
        if (!this.formInfo.customFields) {
          this.formInfo.customFields = [];
        }

        // customFieldAnswers 초기화
        this.formInfo.customFields.forEach((field, i) => {
          if (!Object.prototype.hasOwnProperty.call(this.formData.customFieldAnswers, i)) {
            this.formData.customFieldAnswers[i] = field.type === "checkbox" ? [] : "";
          }
        });

        // rewardAnswers 초기화
        if (Array.isArray(this.formInfo.rewardTiers) && this.formInfo.rewardTiers.length > 0) {
          this.formData.rewardAnswers = this.formInfo.rewardTiers.map(() => ({
            quantity: 0,
          }));
          // 패키지가 있으면 티켓 매수 선택 없이 1매 고정
          if (!this.formData.quantity) {
            this.formData.quantity = 1;
          }
        } else {
          this.formData.rewardAnswers = [];
        }

        // purchaseAnswers 초기화
        if (Array.isArray(this.formInfo.purchaseItems) && this.formInfo.purchaseItems.length > 0) {
          this.formData.purchaseAnswers = this.formInfo.purchaseItems.map((item) => ({
            quantity: item.required ? 1 : 0,
            optionAnswer: item.hasOption
              ? (item.optionType === "checkbox" ? [] : "")
              : null,
          }));
        } else {
          this.formData.purchaseAnswers = [];
        }

      } catch (err) {
        console.error("데이터 로드 오류:", err);
        console.error("오류 상세:", err.message, err.code);
        this.error = `데이터를 불러오는 중 오류가 발생했습니다.\n오류: ${err.message || err
          }`;
      } finally {
        this.loading = false;
      }
    },
    // 예매 기간 포맷팅
    formatReservationPeriod() {
      const start = this.formInfo.reservationStartDate;
      const end = this.formInfo.reservationEndDate;

      const formatDate = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate
          ? timestamp.toDate()
          : new Date(timestamp);
        return `${date.getFullYear()}년 ${date.getMonth() + 1
          }월 ${date.getDate()}일 ${String(date.getHours()).padStart(
            2,
            "0"
          )}:${String(date.getMinutes()).padStart(2, "0")}`;
      };

      if (start && end) {
        return `${formatDate(start)} \n ~ ${formatDate(end)}`;
      } else if (start) {
        return `${formatDate(start)}부터`;
      } else if (end) {
        return `${formatDate(end)}까지`;
      }
      return "";
    },
    // 텍스트 포맷팅 (줄바꿈 처리)
    formatText(text) {
      if (!text) return "";
      return text.replace(/\n/g, "<br>");
    },
    // 커스텀 필드 답변 포맷팅 (view mode용)
    formatCustomFieldAnswer(value, type) {
      if (value === undefined || value === null) return "-";
      if (type === "checkbox" && Array.isArray(value)) {
        return value.length > 0 ? value.join(", ") : "-";
      }
      return String(value);
    },
    // 체크박스 토글
    toggleCheckbox(fieldIndex, option) {
      if (!this.formData.customFieldAnswers[fieldIndex]) {
        this.formData.customFieldAnswers[fieldIndex] = [];
      }
      const answers = this.formData.customFieldAnswers[fieldIndex];
      const index = answers.indexOf(option);
      if (index > -1) {
        answers.splice(index, 1);
      } else {
        answers.push(option);
      }
    },
    // 체크박스 체크 여부 확인
    isCheckboxChecked(fieldIndex, option) {
      const answers = this.formData.customFieldAnswers[fieldIndex];
      return Array.isArray(answers) && answers.includes(option);
    },
    // 추가구매항목 수량 감소
    toggleReward(index) {
      if (this.submitting || this.isPreviewMode || this.isViewMode) return;
      const ans = this.formData.rewardAnswers[index];
      if (!ans) return;
      if (ans.quantity > 0) {
        ans.quantity = 0;
      } else {
        ans.quantity = 1;
      }
    },
    incrementReward(index) {
      const tier = this.formInfo.rewardTiers[index];
      const ans = this.formData.rewardAnswers[index];
      if (!ans) return;
      const max = tier.maxSelection > 0 ? tier.maxSelection : Infinity;
      if (ans.quantity < max) ans.quantity++;
    },
    decrementReward(index) {
      const ans = this.formData.rewardAnswers[index];
      if (!ans) return;
      if (ans.quantity > 0) ans.quantity--;
    },
    decrementPurchase(index) {
      const item = this.formInfo.purchaseItems[index];
      const ans = this.formData.purchaseAnswers[index];
      if (!ans) return;
      const min = item.required ? 1 : 0;
      if (ans.quantity > min) {
        ans.quantity--;
      }
    },
    // 추가구매항목 수량 증가
    incrementPurchase(index) {
      const item = this.formInfo.purchaseItems[index];
      const ans = this.formData.purchaseAnswers[index];
      if (!ans) return;
      const max = item.maxQuantity > 0 ? item.maxQuantity : Infinity;
      if (ans.quantity < max) {
        ans.quantity++;
      }
    },
    // 추가구매항목 체크박스 토글
    togglePurchaseCheckbox(itemIndex, option) {
      const ans = this.formData.purchaseAnswers[itemIndex];
      if (!ans) return;
      if (!Array.isArray(ans.optionAnswer)) ans.optionAnswer = [];
      const idx = ans.optionAnswer.indexOf(option);
      if (idx > -1) ans.optionAnswer.splice(idx, 1);
      else ans.optionAnswer.push(option);
    },
    // 추가구매항목 체크박스 체크 여부
    isPurchaseCheckboxChecked(itemIndex, option) {
      const ans = this.formData.purchaseAnswers[itemIndex];
      return Array.isArray(ans?.optionAnswer) && ans.optionAnswer.includes(option);
    },
    // 상태 텍스트 변환
    getStatusText(status) {
      const statusMap = {
        pending: "대기중",
        confirmed: "확정",
        cancelled: "취소",
        refund_requested: "환불 요청",
        refund_completed: "환불 완료",
      };
      return statusMap[status] || status;
    },
    // DM 보내기
    sendDM() {
      window.open("https://x.com/linkook_live", "_blank");
    },
    async copyAccountNumber(accountNumber) {
      try {
        await navigator.clipboard.writeText(accountNumber);
        this.$alert("계좌번호가 복사되었습니다.");
      } catch (e) {
        console.error("복사 오류:", e);
        this.$alert("복사에 실패했습니다.");
      }
    },
    async handleSubmit() {
      if (!this.isFormValid || this.submitting) return;
      if (this.isPreviewMode || this.isViewMode) return;

      // 중복 제출 방지: 모달 대기 중에도 버튼 비활성화
      this.submitting = true;

      try {
        // 예매 기간 체크 (관리자는 예외)
        if (!this.isAdmin && !this.canSubmitReservationNow) {
          if (this.isReservationClosed) {
            this.$alert("예매가 종료되었습니다.");
          } else {
            this.$alert("아직 예매 기간이 시작되지 않았습니다.");
          }
          return;
        }

        // 입금 확인 모달 표시
        const nickname = this.userStore.nickname || "닉네임";
        const message = `입금완료하셨나요?\n<span style="color:#66bdff;font-weight:600;">"${nickname}"으로 입금해주셔야 입금 확인됩니다.</span>\n<span style="color:#e03131;font-weight:600;font-size:12px;">※ 입금자명을 꼭 다시 한 번 확인해 주세요!</span>`;

        const quantity = Number(this.formData.quantity || 0);
        const rawPrice = this.liveData?.linkookPrice ?? "";
        const toNumber = (v) => {
          if (typeof v === "number") return Number.isFinite(v) ? v : null;
          if (typeof v !== "string") return null;
          const onlyDigits = v.replace(/[^\d]/g, "");
          if (!onlyDigits) return null;
          const n = Number(onlyDigits);
          return Number.isFinite(n) ? n : null;
        };
        const unitPrice = toNumber(rawPrice);
        const ticketTotal = unitPrice && quantity ? unitPrice * quantity : null;
        const goodsTotal = this.purchaseItemsTotal || 0;
        const rewardTotal = this.rewardItemsTotal || 0;
        const totalPrice = (ticketTotal || 0) + goodsTotal + rewardTotal || null;
        const formatWon = (n) => `${new Intl.NumberFormat("ko-KR").format(n)}원`;

        const ok = await this.$confirm(message, {
          type: "ticketPaymentConfirm",
          bankName: "국민은행",
          accountNumber: "009901-04-203973",
          accountHolder: "이민경 (링꾹)",
          amountText: totalPrice ? formatWon(totalPrice) : "",
          amountSubText: "",
        });
        if (!ok) return;

        await this.submitForm();
      } finally {
        // submitForm 내부에서 submitting을 관리하므로,
        // 취소/오류로 submitForm에 진입하지 않은 경우에만 false로 복원
        if (!this.submitSuccess) {
          this.submitting = false;
        }
      }
    },
    // 사용자 예매 정보 로드 (예매 확인 뷰 — regId = 로그인 이메일)
    async loadUserReservation() {
      if (!this.isViewMode || !this.userStore.email) {
        return;
      }

      try {
        const list = await getTicketReservationsByLiveIdAndRegId(
          this.liveId,
          this.userStore.email
        );
        const reservation = list[0];
        if (!reservation) return;

        this.userReservation = {
          id: reservation.id,
          ...reservation,
        };

        this.formData.quantity = this.userReservation.quantity
          ? String(this.userReservation.quantity)
          : "";
        this.formData.customFieldAnswers = this.userReservation.customFieldAnswers || {};
        if (Array.isArray(this.userReservation.purchaseAnswers)) {
          this.formData.purchaseAnswers = this.userReservation.purchaseAnswers;
        }
        this.formData.paymentInfoChecked = true;
      } catch (err) {
        console.error("예매 정보 로드 오류:", err);
      }
    },
    // 폼 제출 (handleSubmit에서만 호출, submitting은 handleSubmit에서 관리)
    async submitForm() {
      try {
        // 링꾹 예매인 경우 티켓 가용성 확인
        if (this.liveData?.linkookTicketCount) {
          const totalReservedTickets = await getTotalReservedQuantityForLive(this.liveId);
          const requestedQuantity = parseInt(this.formData.quantity);
          if (
            totalReservedTickets + requestedQuantity >
            this.liveData.linkookTicketCount
          ) {
            alert("예매 가능 매수가 초과되었습니다.");
            return;
          }
        }

        const reservationData = {
          liveId: this.liveId,
          liveTitle: this.liveData.title,
          name: this.userStore.nickname || this.userStore.email?.split("@")[0] || "",
          email: this.userStore.email || "",
          phone: (this.formData.phone || "").replace(/[^0-9]/g, ""),
          quantity: parseInt(this.formData.quantity),
          userId: this.userStore.uid || null,
          regId: this.userStore.email || null,
          regNickname: this.userStore.nickname || null,
          customFieldAnswers: this.formData.customFieldAnswers || {},
          purchaseAnswers: this.formData.purchaseAnswers || [],
          ticketAmount: this.unitTicketPriceNumber || 0,
          purchaseTotal: this.purchaseItemsTotal || 0,
          rewardAnswers: (this.formData.rewardAnswers || []).reduce((arr, a, i) => { if (a.quantity > 0) arr.push({ tierIndex: i, quantity: a.quantity }); return arr; }, []),
          rewardTotal: this.rewardItemsTotal || 0,
          totalAmount: this.totalDepositAmountNumber || 0,
          status: "pending",
          paymentConfirmed: false,
        };

        await createReservation(reservationData);

        this.submitSuccess = true;
        trackReservationComplete(this.liveId);
        window.scrollTo({ top: 0, behavior: "smooth" });

        setTimeout(() => {
          this.$router.push(`/live/${this.liveId}`);
        }, 2000);
      } catch (err) {
        console.error("예매 신청 오류:", err);
        alert("예매 신청 중 오류가 발생했습니다. 다시 시도해주세요.");
      }
    },
  },
};
</script>

<style scoped>
.ticket-form-container {
  max-width: 95%;
  margin: 0 auto;
  font-family: "NanumSquareRoundR", sans-serif;
  min-height: 100vh;
}

.loading,
.error {
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  animation: fadeIn 0.4s ease-out;
}

.error {
  color: #e74c3c;
  background: rgba(231, 76, 60, 0.1);
  border-radius: 16px;
  padding: 30px;
}

.form-wrapper {
  padding: 24px 0;
  text-align: left;
  max-width: 900px;
  margin: 0 auto;
  animation: slideUp 0.5s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.reservation-confirm-notice {
  background: linear-gradient(135deg, #e6f7ed 0%, #d4edda 100%);
  border: 2px solid #10b981;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  text-align: center;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 18px;
  color: #155724;
  box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2);
  animation: fadeIn 0.5s ease-out;
}

.preview-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #66bdff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  font-family: "NanumSquareRoundR", sans-serif;
  animation: fadeIn 0.5s ease-out;
}

.preview-notice-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: #66bdff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-family: "NanumSquareRoundB", sans-serif;
}

.preview-notice-content {
  flex: 1;
  min-width: 0;
}

.preview-notice-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #66bdff;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.preview-notice-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.reservation-guard {
  margin-top: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.25);
}

.reservation-guard-text {
  margin: 0;
  font-size: 13px;
  color: #b26a00;
  font-family: "NanumSquareRoundB", sans-serif;
}

/* 섹션 블록 (TicketFormAdmin 스타일) */
.form-section-block {
  padding: 32px 0;
  margin-bottom: 0;
  border-bottom: 1px solid #e9ecef;
  animation: fadeIn 0.5s ease-out backwards;
}

.form-section-block:last-of-type {
  border-bottom: none;
}

.cancel-policy-content {
  font-size: 14px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.8;
  padding: 16px 20px;
  background: #f8f9fa;
  border-radius: 10px;
  border-left: 4px solid #66bdff;
  white-space: pre-wrap;
}

.cancel-policy-default-list {
  margin: 0 0 10px 0;
  padding-left: 20px;
  list-style: disc;
}

.cancel-policy-default-list li {
  font-size: 14px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.9;
}

.cancel-policy-default-note {
  margin: 0;
  font-size: 12px;
  color: #999;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 추가구매항목 */
.purchase-items-section {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* 티켓 패키지 카드 */
.reward-tier-card {
  padding: 20px;
  border: 2px solid #e9ecef;
  border-radius: 14px;
  margin-bottom: 14px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fff;
}

.reward-tier-card:hover {
  border-color: #ced4da;
}

.reward-tier-card.reward-selected {
  border-color: rgba(102, 189, 255, 0.7);
  box-shadow: 0 2px 12px rgba(102, 189, 255, 0.15);
  background: linear-gradient(135deg, rgba(102, 189, 255, 0.04) 0%, rgba(255, 105, 180, 0.03) 100%);
}

.reward-tier-images {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  margin-bottom: 12px;
  -webkit-overflow-scrolling: touch;
}

.reward-tier-image {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 10px;
  flex-shrink: 0;
  cursor: pointer;
}

.reward-tier-images::-webkit-scrollbar {
  height: 4px;
}

.reward-tier-images::-webkit-scrollbar-thumb {
  background: #dee2e6;
  border-radius: 2px;
}

.reward-tier-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.reward-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #66bdff, #ff69b4);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.reward-tier-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  color: #495057;
}

.reward-tier-price {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 22px;
  color: #212529;
  margin-bottom: 10px;
}

.reward-tier-desc {
  font-size: 13px;
  color: #868e96;
  margin: 0 0 10px;
}

.reward-tier-items {
  list-style: disc;
  padding-left: 20px;
  margin: 0 0 12px;
}

.reward-tier-items li {
  font-size: 13px;
  color: #495057;
  line-height: 1.8;
}

.reward-qty-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f5;
}

.reward-qty-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  font-size: 16px;
  font-weight: 700;
  color: #495057;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reward-qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.reward-qty-value {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 15px;
  min-width: 24px;
  text-align: center;
}

.reward-subtotal {
  margin-left: auto;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 15px;
  color: #1976d2;
}

.purchase-item-card {
  padding: 20px 0;
  border-bottom: 1px solid #f1f3f5;
}

.purchase-item-card:last-child {
  border-bottom: none;
}

/* 이미지가 있을 때: 이미지 상단 전체폭, 정보 하단 */
.purchase-item-top {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.purchase-item-images-wrapper {
  display: flex;
  flex-direction: row;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: #ddd transparent;
}

.purchase-item-images-wrapper::-webkit-scrollbar {
  height: 4px;
}

.purchase-item-images-wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.purchase-item-images-wrapper::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 2px;
}

.purchase-item-image {
  flex-shrink: 0;
  width: 200px;
  height: 200px;
  object-fit: cover;
  display: block;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
}

.purchase-item-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.purchase-item-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 16px;
  color: #222;
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
}

.purchase-required-badge {
  font-size: 11px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #e53e3e;
  background: rgba(229, 62, 62, 0.08);
  border: 1px solid rgba(229, 62, 62, 0.25);
  border-radius: 4px;
  padding: 1px 7px;
  white-space: nowrap;
}

.purchase-item-desc {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 13px;
  color: #868e96;
  line-height: 1.5;
}

.purchase-item-price {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 17px;
  color: #1a6fa8;
  margin-top: 2px;
}

/* 수량 컨트롤 */
.purchase-qty-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.purchase-qty-control {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid #dee2e6;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.purchase-qty-btn {
  width: 42px;
  height: 42px;
  background: #f8f9fa;
  border: none;
  cursor: pointer;
  font-size: 20px;
  font-weight: 400;
  color: #495057;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s;
  line-height: 1;
}

.purchase-qty-btn:hover:not(:disabled) {
  background: #e9ecef;
  color: #212529;
}

.purchase-qty-btn:disabled {
  color: #ced4da;
  cursor: not-allowed;
  background: #f8f9fa;
}

.purchase-qty-value {
  min-width: 44px;
  text-align: center;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 16px;
  color: #212529;
  background: #fff;
  border-left: 1.5px solid #dee2e6;
  border-right: 1.5px solid #dee2e6;
  padding: 0 6px;
  height: 42px;
  line-height: 42px;
  user-select: none;
}

.purchase-subtotal {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 15px;
  color: #1a6fa8;
}

/* 옵션 영역 */
.purchase-option-area {
  margin-top: 16px;
  padding: 16px;
  border-radius: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.purchase-option-area>label {
  display: block;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  color: #2d3748;
  margin-bottom: 10px;
}

.purchase-option-area>label.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

.purchase-option-area .text-input,
.purchase-option-area .textarea-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: #fff;
}

.purchase-option-area .text-input:focus,
.purchase-option-area .textarea-input:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.purchase-option-area .text-input:disabled,
.purchase-option-area .textarea-input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
  opacity: 0.6;
}

.purchase-option-area .textarea-input {
  resize: vertical;
  min-height: 90px;
}

.purchase-option-area .radio-button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}

.purchase-option-area .radio-option-btn {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  overflow-wrap: anywhere;
  transition: all 0.2s;
}

.purchase-option-area .radio-option-btn.selected {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.purchase-option-area .radio-option-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.purchase-option-area .checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

.purchase-option-area .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  color: #4a5568;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.purchase-option-area .checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #66bdff;
  flex-shrink: 0;
}

@media screen and (min-width: 600px) {
  .purchase-item-info {
    flex: 1;
    min-width: 0;
  }
}

.section-header {
  margin-bottom: 24px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.section-description {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.deposit-nickname-notice {
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
}

.deposit-warning-notice {
  margin: 8px 0 0 0;
  font-size: 12px;
  font-weight: 600;
  color: #e03131;
  font-family: "NanumSquareRoundB", sans-serif;
}

.info-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.info-row {
  display: flex;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}


.info-label {
  flex: 0 0 120px;
  font-weight: 600;
  color: #495057;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
}

.info-value {
  flex: 1;
  color: #212529;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
}

.info-value-account {
  font-family: "NanumSquareRoundB", sans-serif;
  letter-spacing: 0.5px;
  color: #1e40af;
}

.info-value-account-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.account-number-text {
  word-break: break-all;
}

.copy-btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  padding: 6px;
}

.copy-btn-icon:hover {
  background: #e9ecef;
}

.copy-btn-icon:active {
  background: #dee2e6;
}

.copy-btn-icon img {
  width: 16px;
  height: 16px;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.copy-btn-icon:hover img {
  opacity: 0.9;
}

.notice-content {
  color: #4a5568;
  line-height: 1.8;
  font-size: 13px;
  white-space: pre-wrap;
  font-family: "NanumSquareRoundR", sans-serif;
}

.custom-notice-item {
  padding: 16px 0;
  border-bottom: 1px solid #e9ecef;
}

.custom-notice-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.custom-notice-item:first-child {
  padding-top: 0;
}

.custom-notice-title {
  font-size: 14px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 8px 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.auto-include-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 12px 0 0 0;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #66bdff;
  border-radius: 8px;
  font-family: "NanumSquareRoundR", sans-serif;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.auto-include-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: #66bdff;
  color: #fff;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-family: "NanumSquareRoundB", sans-serif;
}

.auto-include-content {
  flex: 1;
  min-width: 0;
}

.auto-include-label {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #66bdff;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 4px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.auto-include-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.5;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  padding-bottom: 24px;
  border-bottom: 3px solid transparent;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  background-clip: text;
  -webkit-background-clip: text;
  position: relative;
}

.form-header::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px;
}

.form-header h1 {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 36px;
  color: #2d3748;
  margin: 0;
  letter-spacing: -0.5px;
}

.back-btn {
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.back-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.back-btn:active {
  transform: translateY(0);
}


/* 예매 상태 정보 */
.status-info {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  animation: fadeIn 0.5s ease-out backwards;
}

.status-notice {
  font-size: 12px;
  color: #718096;
  margin-bottom: 12px;
  text-align: center;
}

.dm-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background-color: #6f6f6f;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 0 auto 28px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.dm-button:hover {
  background-color: #f070a3;
}

.x-icon {
  width: 14px;
  height: 14px;
  filter: brightness(0) invert(1);
}

.status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f1f3f5;
}

.status-row:last-child {
  border-bottom: none;
}

.status-label {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #66bdff;
  font-size: 16px;
}

.status-value {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 10px;
  font-weight: 600;
}

.status-value.status-pending {
  background: linear-gradient(135deg, #fff3cd 0%, #ffe8a1 100%);
  color: #856404;
  border: 1px solid #ffd700;
}

.status-value.status-confirmed {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #4caf50;
}

.status-value.status-cancelled {
  background: linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%);
  color: #721c24;
  border: 1px solid #f44336;
}

.status-value.payment-confirmed {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  color: #155724;
  border: 1px solid #10b981;
}

.status-value:not(.payment-confirmed):not(.status-pending):not(.status-confirmed):not(.status-cancelled) {
  background: linear-gradient(135deg, #f8f9ff 0%, #fff8fb 100%);
  color: #718096;
  border: 1px solid rgba(102, 126, 234, 0.2);
}


.form-group {
  margin-bottom: 28px;
}

.form-group label {
  display: block;
  margin-bottom: 10px;
  font-family: "NanumSquareRoundB", sans-serif;
  color: #2d3748;
  font-size: 15px;
  letter-spacing: -0.2px;
}

.label-hint {
  font-size: 11px;
  font-weight: normal;
  color: #999;
  margin-left: 6px;
}

.form-group label.required::after {
  content: "*";
  color: #ff4444;
  font-size: 12px;
  font-weight: bold;
  margin-left: 4px;
  vertical-align: top;
  line-height: 1;
}

.form-group input[type="text"],
.form-group input[type="email"],
.form-group input[type="tel"],
.form-group select,
.form-group textarea {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}

.form-group input[type="text"]:focus,
.form-group input[type="email"]:focus,
.form-group input[type="tel"]:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.form-group input:disabled,
.form-group select:disabled,
.form-group textarea:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
  opacity: 0.6;
}

.form-group textarea {
  resize: vertical;
  min-height: 110px;
}

.email-notice {
  margin-top: 8px;
  font-size: 13px;
  color: #718096;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 티켓 매수 버튼 그룹 */
.quantity-button-group {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.total-deposit-section .info-box {
  /* background: linear-gradient(135deg, rgba(102, 189, 255, 0.12) 0%, rgba(255, 105, 180, 0.08) 100%); */
  border: 1px solid rgba(102, 189, 255, 0.25);
}

.total-deposit-compact {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.total-deposit-amount {
  font-size: 18px;
  font-family: "NanumSquareRoundEB", sans-serif;
  color: #222;
  letter-spacing: -0.2px;
}

.total-deposit-calc {
  font-size: 12px;
  color: #64748b;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 구매 내역 요약 */
.order-summary {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f3f5;
}

.order-summary-title {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 13px;
  color: #868e96;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.order-summary-row {
  padding: 8px 0;
  border-bottom: 1px solid #f8f9fa;
}

.order-summary-row:last-of-type {
  border-bottom: none;
}

.order-summary-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 13px;
  color: #495057;
  margin-bottom: 2px;
}

.order-summary-detail {
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 12px;
  color: #868e96;
}

.order-summary-detail strong {
  color: #495057;
  font-family: "NanumSquareRoundEB", sans-serif;
}

.order-summary-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0 4px;
  margin-top: 4px;
  border-top: 1px dashed #dee2e6;
}

.order-summary-total-label {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 14px;
  color: #212529;
}

.order-summary-total-value {
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 16px;
  color: #1976d2;
}

.quantity-btn {
  flex: 1;
  min-width: 0;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  transition: all 0.2s;
}


.quantity-btn.selected {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.quantity-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.quantity-limit-hint {
  font-size: 12px;
  font-weight: 400;
  color: #868e96;
}

.quantity-btn.disabled-by-limit:disabled {
  opacity: 0.4;
  background: #e9ecef;
  color: #adb5bd;
  border-color: #dee2e6;
}

/* 라디오 버튼 그룹 */
.radio-group {
  display: flex;
  /* gap: 24px; */
  flex-wrap: wrap;
  margin-top: 12px;
}

.radio-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 15px;
  color: #4a5568;
  transition: all 0.2s ease;
}

.radio-label:hover .radio-text {
  color: #2d3748;
}

/* 커스텀 질문 섹션 */
.custom-questions-section .custom-fields-section {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.custom-fields-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.custom-field-item .text-input,
.custom-field-item .textarea-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}

.custom-field-item .text-input:focus,
.custom-field-item .textarea-input:focus {
  outline: none;
  border-color: #66bdff;
  box-shadow: 0 0 0 3px rgba(102, 189, 255, 0.1);
}

.custom-field-item .textarea-input {
  resize: vertical;
  min-height: 100px;
}

.custom-field-item .text-input:disabled,
.custom-field-item .textarea-input:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
  opacity: 0.6;
}

.custom-field-radio .radio-button-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.custom-field-radio .radio-option-btn {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #fff;
  color: #475569;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  overflow-wrap: anywhere;
  max-width: 100%;
  transition: all 0.2s;
}

.custom-field-radio .radio-option-btn.selected {
  background: #66bdff;
  border-color: #66bdff;
  color: #fff;
}

.custom-field-radio .radio-option-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.custom-field-checkbox .checkbox-group {
  margin-top: 8px;
}

.field-description {
  font-size: 13px;
  color: #6c757d;
  margin: -4px 0 8px 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.custom-field-image {
  display: block;
  width: 100%;
  max-width: 480px;
  border-radius: 10px;
  margin: 6px 0 10px 0;
  object-fit: cover;
}

.checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
}

.checkbox-label {
  display: inline-flex !important;
  align-items: center;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  font-size: 15px;
  color: #4a5568;
  transition: all 0.2s ease;
  gap: 10px;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.checkbox-label:hover {
  color: #2d3748;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #66bdff;
  flex-shrink: 0;
}

.radio-label input[type="radio"] {
  appearance: none;
  width: 20px;
  height: 20px !important;
  border: 2px solid #cbd5e0;
  border-radius: 50%;
  margin-right: 8px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.radio-label input[type="radio"]:hover {
  border-color: #66bdff;
}

.radio-label input[type="radio"]:checked {
  border-color: #66bdff;
  border-width: 6px;
}

.radio-label input[type="radio"]:checked~.radio-text {
  color: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
}

.radio-label input[type="radio"]:disabled {
  cursor: not-allowed;
  opacity: 0.5;
  background: #f7fafc;
}

.radio-label:has(input[type="radio"]:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

.radio-text {
  transition: all 0.2s ease;
  user-select: none;
}

/* 개인정보 동의 섹션 */
.privacy-section {
  margin: 32px 0 24px;
  padding: 24px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.privacy-header h3 {
  margin: 0 0 20px 0;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 17px;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 8px;
}

.privacy-header h3::before {
  content: "🔒";
  font-size: 20px;
}

.privacy-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #e9ecef;
}

.privacy-item {
  margin-bottom: 12px;
  font-size: 14px;
  line-height: 1.6;
  color: #4a5568;
}

.privacy-item:last-of-type {
  margin-bottom: 0;
}

.privacy-item strong {
  color: #66bdff;
  font-family: "NanumSquareRoundB", sans-serif;
  margin-right: 8px;
}

.privacy-notice {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e2e8f0;
  font-size: 13px;
  color: #718096;
  line-height: 1.7;
}

.privacy-agreement {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.agreement-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 15px;
  margin: 0;
}

.agreement-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #66bdff;
}

.agreement-label span {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #2d3748;
}

.payment-section {
  background: linear-gradient(135deg, #fff8f0 0%, #fff4f8 100%);
  border: 2px solid rgba(234, 179, 102, 0.3);
}

.payment-section .privacy-header h3::before {
  content: "💳";
  font-size: 20px;
  margin-right: 8px;
}

.payment-info-content {
  color: #4a5568;
  line-height: 1.8;
  font-size: 14px;
  white-space: pre-wrap;
}

.help-text {
  margin-top: 10px;
  font-size: 13px;
  color: #718096;
  display: flex;
  align-items: center;
  gap: 6px;
}

.help-text::before {
  content: "💡";
  font-size: 14px;
}

.checkbox-group {
  margin-top: 20px;
  padding: 16px 0;
  border-radius: 8px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 15px;
}

.checkbox-label input[type="checkbox"] {
  width: 22px;
  height: 22px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #66bdff;
}

.checkbox-label span {
  font-family: "NanumSquareRoundB", sans-serif;
  color: #2d3748;
}

.form-actions {
  margin-top: 40px;
  text-align: center;
}

.submit-btn {
  padding: 14px 32px;
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: "NanumSquareRoundEB", sans-serif;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background: #4da3ff;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.submit-btn.preview-disabled {
  opacity: 1;
  cursor: default;
  pointer-events: none;
}

.success-message {
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(72, 187, 120, 0.4);
  z-index: 1000;
  text-align: center;
  animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(255, 255, 255, 0.3);
  min-width: 250px;
}

.success-message p {
  margin: 6px 0;
  font-family: "NanumSquareRoundB", sans-serif;
  font-size: 14px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.success-message p:first-child {
  font-size: 15px;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

/* ========================================
   모바일 (480px 이하)
   ======================================== */
@media (max-width: 480px) {
  .ticket-form-container {
    max-width: 100%;
  }

  .loading,
  .error {
    padding: 40px 10px;
    font-size: 14px;
  }

  .error {
    padding: 20px;
    border-radius: 10px;
  }

  .form-wrapper {
    padding: 20px 15px;
  }

  .reservation-confirm-notice,
  .preview-notice {
    padding: 16px;
    margin-bottom: 20px;
    font-size: 14px;
    border-radius: 12px;
  }

  .form-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
  }

  .form-header h1 {
    font-size: 22px;
  }

  .back-btn {
    padding: 8px 16px;
    font-size: 12px;
    border-radius: 8px;
    background-color: #66bdff;
    color: white;
    border: none;
  }

  .info-section,
  .form-section {
    padding: 0;
    margin-bottom: 18px;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .info-section h2,
  .form-section h2 {
    font-size: 17px;
    gap: 8px;
  }

  .info-section h2::before,
  .form-section h2::before {
    width: 3px;
    height: 17px;
  }

  .info-content {
    padding: 16px 12px;
    border-radius: 12px;
  }

  .info-row {
    padding: 8px 0;
  }

  .info-label {
    flex: 0 0 80px;
    font-size: 12px;
  }

  .info-value {
    font-size: 13px;
  }

  .info-section-title {
    font-size: 14px;
    padding-bottom: 8px;
    margin-bottom: 10px;
  }

  .info-description {
    font-size: 13px;
    line-height: 1.6;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-group label {
    font-size: 13px;
    margin-bottom: 6px;
  }

  .form-group label.required::after {
    font-size: 12px;
  }

  .form-group input[type="text"],
  .form-group input[type="email"],
  .form-group input[type="tel"],
  .form-group select,
  .form-group textarea {
    padding: 10px 14px;
    font-size: 13px;
    border-radius: 8px;
    border-width: 1.5px;
  }

  .form-group textarea {
    min-height: 90px;
  }

  .custom-field-radio .radio-button-group {
    flex-direction: column;
  }

  .email-notice {
    font-size: 11px;
    margin-top: 6px;
  }

  .quantity-button-group {
    margin-top: 8px;
    gap: 6px;
  }

  .quantity-btn {
    padding: 10px 8px;
    font-size: 12px;
  }

  .radio-group {
    margin-top: 8px;
    gap: 4px;
  }

  .radio-label {
    font-size: 13px;
  }

  .radio-label input[type="radio"] {
    width: 16px;
    height: 16px !important;
    margin-right: 5px;
  }

  .custom-fields-section {
    margin-top: 18px;
    padding-top: 18px;
  }

  .field-description {
    font-size: 11px;
  }

  .checkbox-group {
    gap: 8px;
  }

  .checkbox-label {
    font-size: 13px;
    gap: 6px;
  }

  .checkbox-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
  }

  .privacy-section {
    margin: 24px 0 20px;
    padding: 18px 14px;
    border-radius: 12px;
  }

  .privacy-header h3 {
    font-size: 14px;
    margin-bottom: 14px;
  }

  .privacy-header h3::before {
    font-size: 16px;
  }

  .privacy-content {
    padding: 14px;
    border-radius: 8px;
    margin-bottom: 14px;
  }

  .privacy-item {
    font-size: 12px;
    margin-bottom: 8px;
    line-height: 1.5;
  }

  .privacy-item strong {
    font-size: 12px;
    margin-right: 6px;
  }

  .privacy-notice {
    font-size: 11px;
    margin-top: 12px;
    padding-top: 12px;
    line-height: 1.6;
  }

  .privacy-agreement {
    padding: 12px;
    border-radius: 8px;
  }

  .agreement-label {
    font-size: 13px;
  }

  .agreement-label input[type="checkbox"] {
    width: 16px;
    height: 16px;
    margin-right: 8px;
  }

  .payment-info-section h2::before {
    font-size: 16px;
  }

  .payment-info-box {
    padding: 16px;
  }

  .account-number {
    font-size: 13px;
  }

  .payment-section .privacy-header h3::before {
    font-size: 16px;
  }

  .payment-info-content {
    font-size: 12px;
    line-height: 1.6;
  }

  .form-actions {
    margin-top: 26px;
  }

  .submit-btn {
    width: 100%;
    padding: 14px 20px;
    font-size: 15px;
    border-radius: 12px;
  }

  .status-info {
    padding: 6px 14px;
    margin-bottom: 6px;
    border-radius: 12px;
  }

  .status-row {
    padding: 12px 0;
  }

  .status-label,
  .status-value {
    font-size: 13px;
  }

  .status-value {
    padding: 5px 12px;
    border-radius: 6px;
  }

  .status-notice {
    font-size: 11px;
    margin-bottom: 10px;
  }

  .dm-button {
    padding: 6px 12px;
    font-size: 10px;
    border-radius: 5px;
    margin-bottom: 20px;
    gap: 5px;
  }

  .x-icon {
    width: 11px;
    height: 11px;
  }

  .success-message {
    top: 20px;
    padding: 10px 16px;
    border-radius: 8px;
    min-width: 200px;
  }

  .success-message p {
    font-size: 12px;
    margin: 4px 0;
  }

  .success-message p:first-child {
    font-size: 13px;
  }

  .checkbox-group {
    margin-top: 20px;
    padding: 16px;
    border-radius: 10px;
  }

  .checkbox-label {
    font-size: 13px;
  }

  .checkbox-label input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-right: 10px;
  }

  .help-text {
    font-size: 11px;
    margin-top: 8px;
  }

  .help-text::before {
    font-size: 12px;
  }
}

/* 이미지 확대 가능 커서 */
.zoomable {
  cursor: zoom-in;
}

/* 라이트박스 */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.lightbox-img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.5);
  display: block;
  user-select: none;
}

.lightbox-close {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 10000;
  backdrop-filter: blur(4px);
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
